import { useCallback, useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatDate } from "@/lib/labels";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ClipboardList, AlertTriangle, Trash2 } from "lucide-react";
import { usePermissions } from "@/hooks/usePermissions";
import HistoryFiltersBar from "@/components/HistoryFiltersBar";
import HistoryExcludeDialog from "@/components/HistoryExcludeDialog";
import {
  buildHistoryParams,
  getDefaultFilters,
  periodLabel,
  HistoryPagination,
} from "@/components/HistoryFilters";

/**
 * HISTÓRICO DE MONITORAMENTOS — PGA.
 *
 * REGRA: todo monitoramento concluído é histórico permanente. A ocorrência
 * é uma consequência possível, nunca uma condição para o registro existir.
 *
 * O padrão é "Todo o histórico": nenhum filtro default esconde registros
 * antigos (a causa raiz auditada do "aparece hoje e some amanhã").
 */
export default function PgaHistoryPanel({
  clientId,
  unitId,
  structures,
  onOpenDetail,
}) {
  // Autorização por PERMISSÃO (nunca por nome de cargo/perfil).
  const { can } = usePermissions();
  const canDelete = can("pga_historico", "delete") || can("pga_historico", "restore");
  const canRestore = can("pga_historico", "restore");

  const [filters, setFilters] = useState(getDefaultFilters);
  const [meta, setMeta] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [excludeTarget, setExcludeTarget] = useState(null);

  // PERFORMANCE (auditoria): `index.js` renderiza sob React.StrictMode, que
  // executa efeitos DUAS vezes em desenvolvimento. Sem esta guarda, abrir a
  // tela disparava 2 requisições idênticas a /pga/monitorings.
  const requestIdRef = useRef(0);

  const load = useCallback(async () => {
    const requestId = ++requestIdRef.current;

    setLoading(true);
    setError(null);

    try {
      const extra = {};
      if (clientId && clientId !== "todos") extra.client_id = clientId;
      if (unitId && unitId !== "todas") extra.unit_id = unitId;

      const { data } = await api.get("/pga/monitorings", {
        params: buildHistoryParams(filters, extra),
      });

      // Resposta obsoleta (filtro mudou durante o fetch) é descartada.
      if (requestId !== requestIdRef.current) return;

      setItems(data.items || []);
      setMeta({
        total: data.total || 0,
        page: data.page || 1,
        pages: data.pages || 0,
        page_size: data.page_size || 50,
      });
    } catch (err) {
      if (requestId !== requestIdRef.current) return;

      setItems([]);
      setMeta(null);
      setError(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Não foi possível carregar o histórico."
      );
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [filters, clientId, unitId]);

  useEffect(() => {
    load();
  }, [load]);

  const setPage = (page) => setFilters((f) => ({ ...f, page }));

  const extraSelects = structures?.length ? (
    <Select
      value={filters.technicianId || "todos"}
      onValueChange={(v) =>
        setFilters((f) => ({ ...f, technicianId: v === "todos" ? "" : v, page: 1 }))
      }
    >
      <SelectTrigger className="w-44 h-9" data-testid="pga-history-structure">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="todos">Todas as estruturas</SelectItem>
        {structures.map((s) => (
          <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  ) : null;

  return (
    <Card className="border-border bg-card">
      <div className="p-4 border-b border-border space-y-3">
        <div>
          <p className="font-heading font-semibold text-sm flex items-center gap-2">
            <ClipboardList size={14} /> Histórico de monitoramentos
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {meta ? `${meta.total} registro(s)` : "Carregando"} · {periodLabel(filters)}
          </p>
        </div>

        <HistoryFiltersBar
          filters={filters}
          onChange={setFilters}
          extraSelects={extraSelects}
          testId="pga-history"
        />
      </div>

      {error && (
        <p className="p-4 text-sm text-destructive" data-testid="pga-history-error">{error}</p>
      )}

      <div className="divide-y divide-border/50">
        {items.map((m) => (
          <PgaHistoryRow
            key={m.id}
            monitoring={m}
            onOpenDetail={onOpenDetail}
            onExclude={setExcludeTarget}
            canDelete={canDelete || canRestore}
          />
        ))}

        {!loading && !error && items.length === 0 && (
          <p className="p-6 text-center text-muted-foreground text-sm" data-testid="pga-history-empty">
            Nenhum monitoramento registrado neste período.
          </p>
        )}

        {loading && (
          <div className="p-6 flex justify-center">
            <div className="h-6 w-6 rounded-full border-2 border-primary border-t-transparent animate-spin" />
          </div>
        )}
      </div>

      <HistoryPagination meta={meta} onPage={setPage} testId="pga-history" />

      <HistoryExcludeDialog
        open={!!excludeTarget}
        onOpenChange={(v) => !v && setExcludeTarget(null)}
        record={excludeTarget}
        module="pga"
        canDelete={canDelete}
        canRestore={canRestore}
        onDone={load}
      />
    </Card>
  );
}
function PgaHistoryRow({ monitoring: m, onOpenDetail, onExclude, canDelete }) {
  const especies = [
    ...new Set((m.captures || []).map((c) => c.species_name).filter(Boolean)),
  ];

  return (
    <div
      className="flex items-center gap-2 hover:bg-accent transition-colors"
      data-testid={`pga-history-row-${m.id}`}
    >
      <button onClick={() => onOpenDetail(m.id)} className="flex-1 text-left p-4 space-y-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-medium truncate">
            {m.structure_name} <span className="text-xs text-muted-foreground">({m.type_name})</span>
          </p>
          <Badge variant="outline" className="text-[10px]">{m.status || "concluido"}</Badge>
        </div>

        <p className="text-xs text-muted-foreground" data-testid={`pga-history-date-${m.id}`}>
          Executado: {formatDate(m.performed_at || m.finished_at)}
          {m.synced_at && m.performed_at !== m.synced_at
            ? ` · Sincronizado: ${formatDate(m.synced_at)}`
            : ""}
        </p>

        <p className="text-xs text-muted-foreground">
          {m.inspector_name || "Técnico não informado"}
          {m.temperature != null ? ` · ${m.temperature}°C` : ""}
          {m.humidity != null ? ` · ${m.humidity}% UR` : ""}
        </p>

        <div className="flex items-center gap-1.5 flex-wrap pt-0.5">
          <Badge variant="outline">{m.nc_count || 0} NC</Badge>
          <Badge variant="outline" className="bg-yellow-500/10 text-yellow-500 border-yellow-500/30">
            {m.captures_total || 0} captura(s)
          </Badge>
          {especies.length > 0 && (
            <Badge variant="outline" className="bg-amber-500/10 text-amber-500 border-amber-500/30">
              {especies.join(", ")}
            </Badge>
          )}
          {m.template_version ? (
            <Badge variant="outline" className="text-[10px]">checklist v{m.template_version}</Badge>
          ) : null}
        </div>
      </button>

      <div className="pr-3 flex items-center gap-1 shrink-0">
        {m.deleted && (
          <Badge
            variant="outline"
            className="bg-amber-500/10 text-amber-500 border-amber-500/30"
            data-testid={`pga-history-deleted-${m.id}`}
          >
            Excluído
          </Badge>
        )}

        {(m.occurrence_protocols || []).length > 0 ? (
          <Badge
            variant="outline"
            className="bg-red-500/10 text-red-400 border-red-500/30"
            data-testid={`pga-history-occurrence-${m.id}`}
          >
            <AlertTriangle size={11} className="mr-1" />
            {m.occurrence_protocols.join(", ")}
          </Badge>
        ) : (
          <span
            className="text-[11px] text-muted-foreground px-2 py-1"
            data-testid={`pga-history-no-occurrence-${m.id}`}
          >
            Sem ocorrência
          </span>
        )}

        {canDelete && onExclude && (
          <button
            onClick={() => onExclude(m)}
            className="h-7 w-7 rounded-md inline-flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-accent transition-colors"
            title={m.deleted ? "Restaurar registro" : "Excluir registro"}
            data-testid={`pga-history-exclude-${m.id}`}
          >
            <Trash2 size={13} />
          </button>
        )}
      </div>
    </div>
  );
}